'use client'

import { ExternalLink, Inbox, Paperclip } from 'lucide-react'

import { useMissionSubmissions } from '../hooks/useMissionSubmissions'
import { formatDateTime, isUrl } from '../lib/content-history-labels'
import { getSubmissionStatus } from '../lib/submission-status'
import type { MissionSubmissionEntry } from '../types'

import { Badge } from '@/shared/ui/badge'
import { Dialog } from '@/shared/ui/dialog'
import { Modal, ModalDescription, ModalTitle } from '@/shared/ui/Modal'

function StatusBadge({ status }: { status: MissionSubmissionEntry['status'] }) {
  const { name, icon: Icon, variant } = getSubmissionStatus(status)
  return (
    <Badge variant={variant}>
      <Icon className="mr-1 size-3" aria-hidden />
      {name}
    </Badge>
  )
}

function SubmissionMaterial({ submission }: { submission: MissionSubmissionEntry }) {
  const { media, content } = submission

  if (media?.url) {
    return (
      <img
        src={media.url}
        alt={media.name ?? 'Material enviado'}
        className="max-h-64 w-full rounded-xl border-2 border-border object-contain"
      />
    )
  }

  if (content && isUrl(content)) {
    return (
      <a
        href={content}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 break-all rounded-full bg-primary/10 px-3 py-1.5 text-sm font-bold text-primary transition-colors hover:bg-primary/20"
      >
        <ExternalLink className="size-3.5 shrink-0" aria-hidden />
        {content}
      </a>
    )
  }

  if (content) {
    return (
      <p className="whitespace-pre-wrap break-words rounded-xl bg-muted/60 px-3 py-2 text-sm font-medium leading-relaxed text-primary-dark">
        {content}
      </p>
    )
  }

  return (
    <p className="inline-flex items-center gap-1.5 text-sm font-semibold italic text-muted-foreground">
      <Paperclip className="size-3.5" aria-hidden />
      Sem material enviado.
    </p>
  )
}

interface MissionSubmissionsModalProps {
  missionId: number | null
  missionName: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function MissionSubmissionsModal({
  missionId,
  missionName,
  open,
  onOpenChange
}: MissionSubmissionsModalProps) {
  const { submissions, isLoading } = useMissionSubmissions(open ? missionId : null)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Modal className="sm:max-w-lg">
        <ModalTitle className="flex items-center gap-2">
          <Paperclip className="size-5 text-primary" aria-hidden />
          {missionName ? `Envios — ${missionName}` : 'Envios da missão'}
        </ModalTitle>
        <ModalDescription>
          Seus envios para esta missão nesta temporada, com o material enviado.
        </ModalDescription>

        {isLoading ? (
          <p className="py-8 text-center text-sm font-semibold text-muted-foreground">
            Carregando envios…
          </p>
        ) : submissions.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <span className="flex size-12 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
              <Inbox className="size-6" strokeWidth={1.5} aria-hidden />
            </span>
            <p className="text-sm font-semibold italic text-muted-foreground">
              Nenhum envio registrado para esta missão.
            </p>
          </div>
        ) : (
          <div className="mt-2 flex flex-col gap-3">
            {submissions.map(submission => (
              <div
                key={submission.id}
                className="flex flex-col gap-3 rounded-2xl border border-border bg-white/70 p-4 shadow-[0_2px_0_rgba(16,89,216,0.12)]"
              >
                <div className="flex items-center justify-between gap-3">
                  <StatusBadge status={submission.status} />
                  <span className="text-xs font-semibold text-muted-foreground">
                    {formatDateTime(submission.created_at)}
                  </span>
                </div>
                <SubmissionMaterial submission={submission} />
              </div>
            ))}
          </div>
        )}
      </Modal>
    </Dialog>
  )
}
